import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReviewCard from '../common/ReviewCard';
import ProductCardSkeleton from '../common/ProductCardSkeleton';
import { EmptyState, ErrorState } from '../common/States';
import { apiUrl } from '../../config/api';

/** Highlighted customer reviews. `layout="rail"` scrolls horizontally. */
const BestReviews = ({ layout = 'grid' }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl('/api/fetch-best-reviews'));
      if (!response.ok) throw new Error('Failed to fetch reviews');
      const data = await response.json();
      if (!isMounted.current) return;
      setReviews(data?.reviews || []);
      setError(null);
    } catch {
      if (isMounted.current) setError('We could not load reviews right now.');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchReviews();
    return () => {
      isMounted.current = false;
    };
  }, [fetchReviews]);

  const className = layout === 'rail' ? 'ij-rail' : 'ij-product-grid';

  if (loading) {
    return (
      <div className={className} aria-busy="true">
        {Array.from({ length: 4 }, (_, index) => (
          <ProductCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (error) return <ErrorState description={error} onRetry={fetchReviews} />;

  if (reviews.length === 0) {
    return (
      <EmptyState
        title="No reviews yet"
        description="Be the first to share what you think about a piece."
      />
    );
  }

  return (
    <div className={className}>
      {reviews.map((review) => (
        <ReviewCard key={review.id} review={review} />
      ))}
    </div>
  );
};

export default BestReviews;
